import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { arrayMove } from '@dnd-kit/sortable';
import { enqueueSnackbar } from 'notistack';
import { useConfirm } from 'material-ui-confirm';
import { translate as t } from 'hooks/useTranslations';
import { AWB, AccreditationStandardNew } from '../../../../_types/awb';
import {
  reorderStandards,
  ReorderStandardsData,
} from '../../../../_data_sources/AccreditationStandards/manageStandard';

import { useStandardsContext } from '../context';
import { ServerErrorResponse } from 'data_sources/constants';

interface ManageStructureContextProps {
  reorderedVersion: AWB.Version | null;
  originalVersion: AWB.Version | null;
  selectedStandard: string | null;
  selectedSubStandard: string | null;
  isSaving: boolean;
  error: string | null;
  isLoadingDraftContent: boolean;
  handleStandardClick: (standardId: string) => void;
  handleSubStandardClick: (subStandardId: string) => void;
  handleDragEndStandards: (event: any) => void;
  handleDragEndSubStandards: (event: any) => void;
  handleDragEndRequirements: (event: any) => void;
  handleSave: () => Promise<void>;
  getFilteredItems: (type: 'standards' | 'subStandards' | 'requirements') => any[];
  getDraftContentValue: (item: AWB.Standard | AWB.SubStandard | AWB.Requirement) => string;
}

const ManageStructureContext = createContext<ManageStructureContextProps | undefined>(undefined);

export const ManageStructureProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { id: accreditationId, versionId } = useParams();
  const { version: originalVersion, isLoading: standardsLoading } = useStandardsContext();

  const [reorderedVersion, setReorderedVersion] = useState<AWB.Version | null>(null);
  const [selectedStandard, setSelectedStandard] = useState<string | null>(null);
  const [selectedSubStandard, setSelectedSubStandard] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    try {
      if (originalVersion) {
        setReorderedVersion(JSON.parse(JSON.stringify(originalVersion)));
        setError(null);
      }
    } catch (err) {
      console.error('Error setting reorderedVersion:', err);
      setError('Error loading standards data');
    }
  }, [originalVersion]);

  const handleStandardClick = (standardId: string) => {
    setSelectedStandard(selectedStandard === standardId ? null : standardId);
    setSelectedSubStandard(null);
  };

  const handleSubStandardClick = (subStandardId: string) => {
    setSelectedSubStandard(selectedSubStandard === subStandardId ? null : subStandardId);
  };

  const getFilteredItems = useCallback(
    (type: 'standards' | 'subStandards' | 'requirements') => {
      if (!reorderedVersion) return [];

      switch (type) {
        case 'standards':
          return reorderedVersion.standards || [];

        case 'subStandards':
          return reorderedVersion.standards?.find((s) => s.id === selectedStandard)?.subStandards || [];

        case 'requirements':
          return (
            reorderedVersion.standards
              ?.find((s) => s.id === selectedStandard)
              ?.subStandards?.find((s) => s.id === selectedSubStandard)?.subStandards || []
          );

        default:
          return [];
      }
    },
    [reorderedVersion, selectedStandard, selectedSubStandard]
  );

  const handleDragEndStandards = (event: any) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setReorderedVersion((prevVersion) => {
        if (!prevVersion) return prevVersion;
        const oldIndex = prevVersion.standards.findIndex((s) => s.id === active.id);
        const newIndex = prevVersion.standards.findIndex((s) => s.id === over.id);
        const newStandards = arrayMove(prevVersion.standards, oldIndex, newIndex);
        return { ...prevVersion, standards: newStandards };
      });
    }
  };

  const handleDragEndSubStandards = (event: any) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !selectedStandard) return;

    setReorderedVersion((prevVersion) => {
      if (!prevVersion) return prevVersion;
      const standardIndex = prevVersion.standards.findIndex((s) => s.id === selectedStandard);
      if (standardIndex === -1) return prevVersion;

      const updatedVersion = JSON.parse(JSON.stringify(prevVersion));
      const standard = updatedVersion.standards[standardIndex];

      const oldIndex = standard.subStandards?.findIndex((s: any) => s.id === active.id) ?? -1;
      const newIndex = standard.subStandards?.findIndex((s: any) => s.id === over.id) ?? -1;

      if (oldIndex !== -1 && newIndex !== -1) {
        standard.subStandards = arrayMove(standard.subStandards, oldIndex, newIndex);
      }

      return updatedVersion;
    });
  };

  const handleDragEndRequirements = (event: any) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !selectedStandard || !selectedSubStandard) return;

    setReorderedVersion((prevVersion) => {
      if (!prevVersion) return prevVersion;
      const standardIndex = prevVersion.standards.findIndex((s) => s.id === selectedStandard);
      if (standardIndex === -1) return prevVersion;

      const updatedVersion = JSON.parse(JSON.stringify(prevVersion));
      const standard = updatedVersion.standards[standardIndex];
      const subStandardIndex = standard.subStandards?.findIndex((s: any) => s.id === selectedSubStandard) ?? -1;

      if (subStandardIndex === -1) return updatedVersion;

      const subStandard = standard.subStandards[subStandardIndex];

      const oldIndex = subStandard.subStandards?.findIndex((r: any) => r.id === active.id) ?? -1;
      const newIndex = subStandard.subStandards?.findIndex((r: any) => r.id === over.id) ?? -1;

      if (oldIndex !== -1 && newIndex !== -1) {
        subStandard.subStandards = arrayMove(subStandard.subStandards, oldIndex, newIndex);
      }

      return updatedVersion;
    });
  };

  const updateStandardPositions = async (standards: AWB.Standard[]) => {
    const reorderData: ReorderStandardsData = {
      standards: [],
    };

    // Collect all standards, substandards, and requirements that need position updates
    standards.forEach((standard, index) => {
      const position = index + 1;
      const currentPosition = Number(standard.position);

      if (currentPosition !== position) {
        reorderData.standards.push({
          id: standard.id,
          position,
        });
      }

      // Collect substandards positions
      if (standard.subStandards) {
        standard.subStandards.forEach((subStandard, subIndex) => {
          const subPosition = subIndex + 1;
          const currentSubPosition = Number(subStandard.position);

          if (currentSubPosition !== subPosition) {
            reorderData.standards.push({
              id: subStandard.id,
              position: subPosition,
            });
          }

          // Collect requirements positions
          if (subStandard.subStandards) {
            subStandard.subStandards.forEach((requirement, reqIndex) => {
              const reqPosition = reqIndex + 1;
              const currentReqPosition = Number(requirement.position);

              if (currentReqPosition !== reqPosition) {
                reorderData.standards.push({
                  id: requirement.id,
                  position: reqPosition,
                });
              }
            });
          }
        });
      }
    });

    // Only make the API call if there are items to reorder
    if (reorderData.standards.length > 0) {
      await reorderStandards(accreditationId!, versionId!, reorderData);
    }
  };

  const handleSave = async () => {
    if (!reorderedVersion) return;

    confirm({
      title: t('awb.standards.manageStructure.saveConfirm.title'),
      description: t('awb.standards.manageStructure.saveConfirm.description'),
      cancellationButtonProps: {
        variant: 'outlined',
        color: 'neutral',
      },
      confirmationButtonProps: {
        variant: 'contained',
        color: 'primary',
      },
      confirmationText: t('common.save'),
      cancellationText: t('common.cancel'),
    }).then(async () => {
      setIsSaving(true);
      try {
        await updateStandardPositions(reorderedVersion.standards);

        await queryClient.invalidateQueries(['accreditation_standards', accreditationId, versionId]);
        await queryClient.refetchQueries(['accreditation_standards', accreditationId, versionId]);

        enqueueSnackbar(t('awb.standards.manageStructure.saveSuccess'), {
          variant: 'success',
        });

        navigate(`/admin/awb/accrediting-bodies/${accreditationId}/versions/${versionId}/standards`);
      } catch (err) {
        let errorMessage = t('awb.standards.manageStructure.saveError');

        if (err && typeof err === 'object' && 'errorInformationCode' in err) {
          const serverError = err as ServerErrorResponse;
          if (serverError.errorDetails && serverError.errorDetails.length > 0) {
            errorMessage = serverError.errorDetails[0].detail || serverError.errorDetails[0].message || errorMessage;
          } else if (serverError.message) {
            errorMessage = serverError.message;
          }

          enqueueSnackbar(errorMessage, { variant: 'error' });
        } else if (err && typeof err === 'object' && 'message' in err) {
          try {
            const parsedError = JSON.parse(err.message as string);
            if (parsedError.errorDetails && parsedError.errorDetails.length > 0) {
              errorMessage = parsedError.errorDetails[0].detail || parsedError.errorDetails[0].message || errorMessage;
            } else if (parsedError.message) {
              errorMessage = parsedError.message;
            }
          } catch {
            errorMessage = typeof err.message === 'string' ? err.message : errorMessage;
          }
          enqueueSnackbar(errorMessage, { variant: 'error' });
        } else {
          enqueueSnackbar(errorMessage, { variant: 'error' });
        }
      }
      setIsSaving(false);
    });
  };

  const getDraftContentValue = useCallback((item: AWB.Standard | AWB.SubStandard | AWB.Requirement): string => {
    return item.draftContent || item.publishedContent || 'Empty content';
  }, []);

  const value: ManageStructureContextProps = {
    reorderedVersion,
    originalVersion: originalVersion || null,
    selectedStandard,
    selectedSubStandard,
    isSaving,
    error,
    isLoadingDraftContent: false,
    handleStandardClick,
    handleSubStandardClick,
    handleDragEndStandards,
    handleDragEndSubStandards,
    handleDragEndRequirements,
    handleSave,
    getFilteredItems,
    getDraftContentValue,
  };

  return <ManageStructureContext.Provider value={value}>{children}</ManageStructureContext.Provider>;
};

export const useManageStructureContext = () => {
  const context = useContext(ManageStructureContext);
  if (!context) {
    throw new Error('useManageStructureContext must be used within a ManageStructureProvider');
  }
  return context;
};
